import { RELATIVE_TIME_OPTIONS, WINDOW_RANGES } from '@/constants/dateTimeRangePicker.constants';
import { DateTimeRangePickerProps, RangeTimeValue } from './types';
import { I18n } from '@lingui/core';
import { Formatters } from '@/hooks/useFormatters';
import dayjs from 'dayjs';

export const formatDateTimeRangeLabel = (
  value: DateTimeRangePickerProps['value'],
  i18n: I18n,
  formatDateTimeRange: Formatters['formatDateTimeRange'],
) => {
  const { start, stop } = value;

  const relativeStart = RELATIVE_TIME_OPTIONS.find((option) => option.value === start);
  if (relativeStart) return i18n._(relativeStart.label);
  const relativeStop = RELATIVE_TIME_OPTIONS.find((option) => option.value === stop);
  if (relativeStop) throw new Error(`Stop time can't be relative`);

  return formatDateTimeRange(new Date(start), stop ? new Date(stop) : null);
};

export const getRelativeTimeValue = (value: DateTimeRangePickerProps['value']) => {
  const { start, stop } = value;
  if (stop !== null) return 'absolute';
  const relativeStart = RELATIVE_TIME_OPTIONS.find((option) => option.value === start);
  if (relativeStart) return relativeStart.value;
  else return 'absolute';
};

export function isRelativeTimeRange(value: RangeTimeValue): boolean {
  return RELATIVE_TIME_OPTIONS.some((option) => option.value === value.start);
}

export function convertTimeRangeToTs(value: RangeTimeValue): { start: number; stop: number } {
  const relative = RELATIVE_TIME_OPTIONS.find((option) => option.value === value.start);
  if (relative == null) {
    return {
      start: Math.floor(dayjs(value.start).valueOf()),
      stop: Math.floor(value.stop ? dayjs(value.stop).valueOf() : dayjs().valueOf()),
    };
  }
  const now = dayjs().valueOf();
  return {
    start: Math.floor(now + relative.numericValue),
    stop: Math.floor(now),
  };
}

export const getWindowForRange = (range: number): string | null => {
  const windowRange = WINDOW_RANGES.find((r) => r.max >= range);
  return windowRange?.window ?? null;
};

export const calculateWindow = (start: dayjs.Dayjs, stop: dayjs.Dayjs | null): string | null => {
  const diff = Math.abs(start.diff(stop ?? dayjs()));
  return getWindowForRange(diff);
};
